if (!customElements.get("slider-component")) {
  customElements.define(
    "slider-component",
    class SliderComponent extends HTMLElement {
      constructor() {
        super();

        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector(
          ".slider-counter--current"
        );
        this.pageTotalElement = this.querySelector(".slider-counter--total");
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        // Handle orientation based on viewport and data attribute
        this.updateOrientation();

        // Add resize event listener to update orientation on viewport changes
        window.addEventListener("resize", this.updateOrientation.bind(this));

        if (!this.slider || !this.nextButton) return;

        this.initPages();

        const resizeObserver = new ResizeObserver((entries) => {
          this.updateOrientation();
          this.initPages();
        });
        resizeObserver.observe(this.slider);

        this.slider.addEventListener("scroll", this.update.bind(this));
        this.prevButton.addEventListener(
          "click",
          this.onButtonClick.bind(this)
        );
        this.nextButton.addEventListener(
          "click",
          this.onButtonClick.bind(this)
        );
      }

      // Method to update orientation based on viewport width
      updateOrientation() {
        const isMobile = !window.matchMedia("(min-width: 750px)").matches;

        // If mobile, always use horizontal orientation regardless of data attribute
        if (isMobile) {
          this.oriantation = "horizontal";
        } else {
          // On desktop, use the orientation from the data attribute if available
          this.oriantation = this.dataset.oriantation || "horizontal";
        }

        // Set position properties based on orientation
        if (this.oriantation === "vertical") {
          this.offsetPosition = "offsetTop";
          this.scrollPosition = "scrollTop";
          this.clientPosition = "clientHeight";
        } else {
          this.offsetPosition = "offsetLeft";
          this.scrollPosition = "scrollLeft";
          this.clientPosition = "clientWidth";
        }
      }

      initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter(
          (element) => element[this.clientPosition] > 0
        );

        if (this.sliderItemsToShow.length < 2) return;

        this.sliderItemOffset =
          this.sliderItemsToShow[1][this.offsetPosition] -
          this.sliderItemsToShow[0][this.offsetPosition];

        this.slidesPerPage = Math.floor(
          (this.slider[this.clientPosition] -
            this.sliderItemsToShow[0][this.offsetPosition]) /
            this.sliderItemOffset
        );

        this.totalPages =
          this.sliderItemsToShow.length - this.slidesPerPage + 1;

        this.update();
      }

      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.updateOrientation();
        this.initPages();
      }

      update() {
        if (!this.slider || !this.nextButton) return;

        const previousPage = this.currentPage;
        const sliderPosition = this.slider[this.scrollPosition];

        this.currentPage =
          Math.round(sliderPosition / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.currentPage;
          this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
          this.dispatchEvent(
            new CustomEvent("slideChanged", {
              detail: {
                currentPage: this.currentPage,
                currentElement: this.sliderItemsToShow[this.currentPage - 1],
              },
            })
          );
        }

        if (this.enableSliderLooping) return;

        // Enhanced check for first and last items, better handling for vertical layouts
        const isAtStart = this.isAtStart();
        const isAtEnd = this.isAtEnd();

        if (isAtStart) {
          this.prevButton.setAttribute("disabled", "disabled");
        } else {
          this.prevButton.removeAttribute("disabled");
        }

        if (isAtEnd) {
          this.nextButton.setAttribute("disabled", "disabled");
        } else {
          this.nextButton.removeAttribute("disabled");
        }
      }

      // New method to check if we're at the start of the slider (first item visible)
      isAtStart() {
        if (!this.sliderItemsToShow.length) return true;

        const firstItem = this.sliderItemsToShow[0];
        const sliderPosition = this.slider[this.scrollPosition];

        // For vertical layouts, we need a small tolerance to account for rounding
        const tolerance = this.oriantation === "vertical" ? 2 : 0;

        return sliderPosition <= firstItem[this.offsetPosition] + tolerance;
      }

      // New method to check if we're at the end of the slider (last item visible)
      isAtEnd() {
        if (!this.sliderItemsToShow.length) return true;

        const lastItem =
          this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
        const sliderPosition = this.slider[this.scrollPosition];
        const viewportSize = this.slider[this.clientPosition];

        // Check if the end of the last slide is visible in the viewport
        return (
          lastItem[this.offsetPosition] + lastItem[this.clientPosition] <=
          sliderPosition + viewportSize + 10
        ); // Add small tolerance
      }

      isSlideVisible(element, offset = 0) {
        if (!element) return false;

        const lastVisibleSlide =
          this.slider[this.clientPosition] +
          this.slider[this.scrollPosition] -
          offset;

        return (
          element[this.offsetPosition] + element[this.clientPosition] <=
            lastVisibleSlide &&
          element[this.offsetPosition] >= this.slider[this.scrollPosition]
        );
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        const sliderPosition = this.slider[this.scrollPosition];

        this.slideScrollPosition =
          event.currentTarget.name === "next"
            ? sliderPosition + step * this.sliderItemOffset
            : sliderPosition - step * this.sliderItemOffset;

        this.setSlidePosition(this.slideScrollPosition);
      }

      setSlidePosition(position) {
        // Ensure we're using the correct orientation
        this.updateOrientation();

        const param =
          this.oriantation === "vertical"
            ? {
                top: position,
                behavior: "smooth",
              }
            : {
                left: position,
                behavior: "smooth",
              };

        this.slider.scrollTo(param);
      }
    }
  );
}
